import { createContext, useContext, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Phone, Plus, ShoppingBag, Trash2, Truck, BadgePercent, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { SITE } from "../data/site";
import { dishImg } from "../data/images";
import { FoodImg } from "./FoodImg";
import { useLang } from "../i18n";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const { t } = useLang();

    const addItem = (dish, qty = 1) => {
        setItems((prev) => {
            const found = prev.find((i) => i.name === dish.name);
            if (found) {
                return prev.map((i) => (i.name === dish.name ? { ...i, qty: i.qty + qty } : i));
            }
            return [...prev, { name: dish.name, price: dish.price, img: dish.img, veg: dish.veg, qty }];
        });
    };

    const setQty = (name, qty) => {
        setItems((prev) =>
            qty <= 0 ? prev.filter((i) => i.name !== name) : prev.map((i) => (i.name === name ? { ...i, qty } : i)),
        );
    };

    const clear = () => setItems([]);

    const { count, total } = useMemo(
        () => ({
            count: items.reduce((s, i) => s + i.qty, 0),
            total: items.reduce((s, i) => s + i.qty * i.price, 0),
        }),
        [items],
    );

    return (
        <CartContext.Provider value={{ items, addItem, setQty, clear, count, total, openCart: () => setOpen(true) }}>
            {children}
            <CartButton count={count} visible={!open} onOpen={() => setOpen(true)} />
            <CartDrawer open={open} setOpen={setOpen} items={items} setQty={setQty} clear={clear} total={total} />
        </CartContext.Provider>
    );
};

const CartButton = ({ count, visible, onOpen }) => {
    const { t } = useLang();
    return (
    <motion.button
        data-testid="cart-floating-button"
        onClick={onOpen}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: visible ? 1 : 0.5, opacity: visible ? 1 : 0 }}
        transition={{ delay: visible ? 0 : 0, duration: 0.3 }}
        whileTap={{ scale: 0.92 }}
        aria-label={`Open cart, ${count} items`}
        className={`fixed right-4 sm:right-6 bottom-24 md:bottom-8 z-40 flex items-center gap-2.5 rounded-full bg-elevated gold-border pl-4 pr-5 py-3.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)] hover:border-saffron/60 transition-colors duration-300 ${
            visible ? "" : "pointer-events-none"
        }`}
    >
        <ShoppingBag className="w-5 h-5 text-saffron-glow" strokeWidth={1.5} />
        <span className="text-xs font-semibold tracking-[0.12em] uppercase text-cream hidden sm:inline">{t("cart.label")}</span>
        <span
            data-testid="cart-count-badge"
            className="min-w-6 h-6 px-1.5 rounded-full bg-saffron text-ink text-xs font-bold flex items-center justify-center"
        >
            {count}
        </span>
    </motion.button>
    );
};

const CartDrawer = ({ open, setOpen, items, setQty, clear, total }) => {
    const { t } = useLang();
    const waHref = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
        t("cart.waIntro") + "\n\n" +
            items.map((i) => `${i.qty} × ${i.name} — ${i.qty * i.price} zł`).join("\n") +
            `\n\n${t("cart.waTotal")}: ${total} zł\n${t("cart.waDeliv")}\n${t("cart.waName")}\n${t("cart.waAddr")}`,
    )}`;

    return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
            data-testid="cart-drawer"
            side="right"
            className="w-full sm:max-w-md bg-onyx border-l border-brass/25 text-cream p-0 flex flex-col"
        >
            <SheetHeader className="px-6 pt-7 pb-5 border-b border-brass/15">
                <SheetTitle className="font-serif text-3xl text-cream text-left">
                    {t("cart.title1")} <span className="italic gold-text">{t("cart.title2")}</span>
                </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5">
                {items.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-20">
                        <ShoppingBag className="w-10 h-10 text-brass/40" strokeWidth={1} />
                        <p className="text-stone-400 text-sm font-light max-w-[220px]">
                            {t("cart.empty")}
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-4">
                        <AnimatePresence initial={false}>
                            {items.map((i) => (
                                <motion.li
                                    key={i.name}
                                    data-testid={`cart-item-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    layout
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: 24 }}
                                    className="flex gap-4 gold-border rounded-xl bg-elevated/50 p-3.5"
                                >
                                    <FoodImg
                                        src={dishImg(i, 200)}
                                        alt={i.name}
                                        className="w-16 h-16 rounded-lg object-cover shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-lg text-cream leading-snug truncate">{i.name}</p>
                                        <p className="text-xs text-stone-400 mt-0.5">{i.price} {t("cart.each")}</p>
                                        <div className="mt-2.5 flex items-center justify-between">
                                            <div className="flex items-center gap-2.5">
                                                <button
                                                    data-testid={`cart-qty-minus-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                    onClick={() => setQty(i.name, i.qty - 1)}
                                                    className="w-9 h-9 rounded-full gold-border flex items-center justify-center hover:border-saffron/60 active:bg-elevated transition-colors duration-200"
                                                    aria-label="Decrease quantity"
                                                >
                                                    {i.qty === 1 ? <Trash2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                                                </button>
                                                <span className="text-sm font-semibold w-5 text-center">{i.qty}</span>
                                                <button
                                                    data-testid={`cart-qty-plus-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                    onClick={() => setQty(i.name, i.qty + 1)}
                                                    className="w-9 h-9 rounded-full gold-border flex items-center justify-center hover:border-saffron/60 active:bg-elevated transition-colors duration-200"
                                                    aria-label="Increase quantity"
                                                >
                                                    <Plus className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <span className="text-sm font-semibold text-saffron-glow">
                                                {i.qty * i.price} zł
                                            </span>
                                        </div>
                                    </div>
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </ul>
                )}
            </div>

            {items.length > 0 && (
                <div className="border-t border-brass/15 px-6 py-6 space-y-4 bg-ink/60">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-stone-400">{t("cart.subtotal")}</span>
                        <span data-testid="cart-total" className="font-serif text-2xl text-cream">
                            {total} zł
                        </span>
                    </div>
                    <div className="space-y-1.5 text-[11px] text-stone-400">
                        <p className="flex items-center gap-2">
                            <BadgePercent className="w-3.5 h-3.5 text-brass shrink-0" />
                            {t("cart.promo1")} {total >= 60 && <span className="text-emerald-400">{t("cart.promo1ok")}</span>}
                        </p>
                        <p className="flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-brass shrink-0" />
                            {total >= 150 ? (
                                <span className="text-emerald-400">{t("cart.freeOk")}</span>
                            ) : (
                                <span>{t("cart.freeToGo").replace("{n}", 150 - total)}</span>
                            )}
                        </p>
                    </div>
                    <a
                        data-testid="cart-call-to-order"
                        href={SITE.phoneHref}
                        className="flex items-center justify-center gap-2.5 w-full rounded-full bg-saffron px-6 py-4 text-sm font-semibold tracking-wide text-ink hover:bg-saffron-bright transition-colors duration-300"
                    >
                        <Phone className="w-4 h-4" />
                        {t("cart.callBtn")} · {SITE.phoneDisplay}
                    </a>
                    <a
                        data-testid="cart-whatsapp-order"
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2.5 w-full rounded-full border border-emerald-500/50 bg-emerald-500/10 px-6 py-4 text-sm font-semibold tracking-wide text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400 transition-[background-color,border-color] duration-300"
                    >
                        <MessageCircle className="w-4 h-4" />
                        {t("cart.waBtn")}
                    </a>
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] text-stone-500">{t("cart.note")}</p>
                        <button
                            data-testid="cart-clear-button"
                            onClick={clear}
                            className="text-[11px] text-stone-500 hover:text-red-400 transition-colors duration-200 underline underline-offset-2"
                        >
                            {t("cart.clear")}
                        </button>
                    </div>
                </div>
            )}
        </SheetContent>
    </Sheet>
    );
};
